import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  IconButton,
  Typography,
  Box,
  MenuItem,
  TextField,
  LinearProgress,
} from '@mui/material';
import { TenantDateField } from '../../../components/TenantDateField';
import { DeleteOutline, CloudUploadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAdmissions } from '../../../hooks/use-admissions';
import { storage } from '../../../lib/firebase';

interface SubmitAdmissionDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  admissionId?: string | undefined;
  programRequirements?: Array<{ type: string; required: boolean; description?: string }>;
}

interface LocalDoc {
  file: File;
  type: string;
  expirationDate?: string;
  uploadProgress?: number;
}

const DOC_TYPES = [
  { value: 'IDENTIFICATION', label: 'Documento de Identidad (DNI/Pasaporte)' },
  { value: 'TRANSCRIPT', label: 'Certificado de Notas / Transcript' },
  { value: 'MEDICAL_RECORD', label: 'Ficha Médica' },
  { value: 'PASTORAL_RECOMMENDATION', label: 'Carta de Recomendación Pastoral' },
  { value: 'OTHER', label: 'Otro' },
];

export function SubmitAdmissionDocumentsDialog({
  open,
  onClose,
  admissionId,
  programRequirements = [],
}: SubmitAdmissionDocumentsDialogProps) {
  const { currentTenantId } = useTenantStore();
  const { submitDocuments, isSubmittingDocs } = useAdmissions();

  // Combine static types with program-specific requirements
  const ALL_DOC_TYPES = [
    ...programRequirements.map((r) => ({
      value: r.type,
      label: `${r.type}${r.required ? ' (Obligatorio)' : ''}`,
    })),
    ...DOC_TYPES.filter((dt) => !programRequirements.some((pr) => pr.type === dt.value)),
  ];

  const [documents, setDocuments] = useState<LocalDoc[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setDocuments((prev) => [
      ...prev,
      ...acceptedFiles.map((f) => ({ file: f, type: '', uploadProgress: 0 })),
    ]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
  });

  const handleRemoveDoc = (index: number) => setDocuments(documents.filter((_, i) => i !== index));

  const handleChange = (index: number, key: keyof LocalDoc, value: any) => {
    const newDocs = [...documents];
    if (newDocs[index]) {
      newDocs[index] = { ...newDocs[index], [key]: value };
      setDocuments(newDocs);
    }
  };

  const handleUploadAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !admissionId) return;

    if (documents.some((d) => !d.type)) {
      setError('Por favor selecciona el Tipo de Documento para todos los archivos.');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const uploadedDocs = await Promise.all(
        documents.map(async (doc, i) => {
          const fileRef = ref(
            storage,
            `tenants/${currentTenantId}/admissions/${admissionId}/${Date.now()}_${doc.file.name}`,
          );
          const uploadTask = uploadBytesResumable(fileRef, doc.file);

          return new Promise<{ type: string; url: string; expirationDate?: string }>(
            (resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  handleChange(i, 'uploadProgress', progress);
                },
                (err) => reject(err),
                async () => {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve({
                    type: doc.type,
                    url: downloadURL,
                    ...(doc.expirationDate ? { expirationDate: doc.expirationDate } : {}),
                  });
                },
              );
            },
          );
        }),
      );

      // Once all uploaded, submit URLs to backend
      await submitDocuments({
        tenantId: currentTenantId,
        admissionId: admissionId,
        documents: uploadedDocs,
      });

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Error al subir los documentos. Por favor intenta de nuevo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setDocuments([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleUploadAndSubmit}>
        <DialogTitle>Subir Documentos</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {!admissionId && (
              <Alert severity="warning">No se ha seleccionado una admisión válida.</Alert>
            )}

            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.default',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.light',
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadOutlined sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.primary">
                {isDragActive ? 'Suelta los archivos aquí' : 'Arrastra y suelta tus archivos aquí'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                o haz clic para seleccionar (PDF, JPG, PNG)
              </Typography>
            </Box>

            {documents.length > 0 && (
              <Stack spacing={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Archivos Seleccionados
                </Typography>
                {documents.map((doc, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={2}>
                      <InsertDriveFileOutlined sx={{ color: 'text.secondary', mt: 0.5 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 1.5 }}>
                          {doc.file.name} ({(doc.file.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            select
                            label="Tipo de Documento *"
                            size="small"
                            fullWidth
                            value={doc.type}
                            onChange={(e) => handleChange(i, 'type', e.target.value)}
                            disabled={isUploading}
                          >
                            <MenuItem value="" disabled>
                              Seleccionar tipo...
                            </MenuItem>
                            {ALL_DOC_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TenantDateField
                            label="Fecha de Vencimiento (Opcional)"
                            size="small"
                            fullWidth
                            value={doc.expirationDate || ''}
                            onChange={(v) => handleChange(i, 'expirationDate', v)}
                            disabled={isUploading}
                          />
                        </Stack>

                        {isUploading && typeof doc.uploadProgress === 'number' && (
                          <Box sx={{ mt: 2 }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={0.5}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Subiendo...
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Math.round(doc.uploadProgress)}%
                              </Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={doc.uploadProgress} />
                          </Box>
                        )}
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveDoc(i)}
                        disabled={isUploading}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={handleClose} disabled={isUploading || isSubmittingDocs}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUploading || isSubmittingDocs || !admissionId || documents.length === 0}
            sx={{ px: 3 }}
          >
            {isUploading
              ? 'Subiendo Archivos...'
              : isSubmittingDocs
                ? 'Guardando...'
                : 'Subir y Enviar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
